import { storeToRefs } from "pinia";
import { RouteRecordRaw, createRouter, createWebHistory } from "vue-router";

import { flushAll } from "@/composables/pending-flush";
import type * as Layouts from "@/layouts";
import mixpanel from "@/lib/mixpanel";
import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { useUserStore } from "@/stores/user-store";

import { platformAdminGuard } from "./platform-admin-guard";

declare module "vue-router" {
    interface RouteMeta {
        requiresAuth?: boolean;
        requiresOrg?: boolean;
        requiresStek?: boolean;
        requiresPlatformAdmin?: boolean;
        layout?: keyof typeof Layouts;
        mode?: "business" | "live" | "dev" | "build";
        // Hides the rail entirely — reserved for the docs/help pages, which
        // have no stek/mode context at all. Prefer `outsideMode` below for any
        // other page: it keeps the rail (and the settings menu that lives in
        // its footer) reachable.
        hideSidebar?: boolean;
        // Outside all three modes (Design/Build/Run) — settings-style pages
        // reached via the settings menu. The rail still renders (org/stek
        // switcher + settings menu stay reachable, so navigating here never
        // strands you without a way back) but with no section highlighted and
        // no submenu panel, since there's no mode/workspace context for it.
        outsideMode?: boolean;
        fullBleed?: boolean;
        preventRedirect?: boolean;
        // The History panel's "Side-by-side" compare only renders on views that
        // actually draw two versions (documents, BPMN diffs). Set it on those
        // routes so the toggle is hidden on rich entity pages where it's a no-op.
        supportsSideBySide?: boolean;
    }
}

const baseRoutes: Array<RouteRecordRaw> = [
    {
        path: "/home",
        name: "home",
        component: () => import("@/views/Home.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/login",
        name: "login",
        component: () => import("@/views/Login.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/oauth/authorize",
        name: "oauth-authorize",
        component: () => import("@/views/OAuthAuthorize.vue"),
        meta: {
            layout: "LoggedOutPageLayout",
            requiresAuth: true,
        },
    },
    {
        // Sign-on hop into a live dev-stack preview: authenticates against
        // bekend's dev-preview enter endpoint, then redirects to the minted
        // preview-gate URL. Preview links shared by the dev agent point here.
        path: "/preview/:host",
        name: "previewEnter",
        component: () => import("@/views/PreviewEnter.vue"),
        meta: {
            layout: "LoggedOutPageLayout",
            requiresAuth: true,
        },
    },
    {
        path: "/signup",
        name: "signup",
        component: () => import("@/views/SignUp.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/privacy-policy",
        name: "privacyPolicy",
        component: () => import("@/views/PrivacyPolicy.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/terms-of-use",
        name: "termsOfUse",
        component: () => import("@/views/TermsOfUse.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/forgot-password",
        name: "forgotPassword",
        component: () => import("@/views/ForgotPassword.vue"),
        meta: { layout: "LoggedOutPageLayout" },
    },
    {
        path: "/billing/success",
        name: "billingSuccess",
        component: () => import("@/views/BillingSuccess.vue"),
        meta: {
            layout: "LoggedOutPageLayout",
            requiresAuth: false,
        },
    },
    {
        path: "/billing/cancel",
        name: "billingCancel",
        component: () => import("@/views/BillingCancel.vue"),
        meta: {
            layout: "LoggedOutPageLayout",
            requiresAuth: false,
        },
    },
    {
        path: "/biz",
        name: "biz",
        component: () => import("@/views/Biz.vue"),
    },
    {
        path: "/ops",
        name: "ops",
        component: () => import("@/views/Ops.vue"),
    },
    {
        path: "/platform-admin",
        name: "platformAdmin",
        component: () => import("@/views/PlatformAdmin.vue"),
        meta: {
            requiresAuth: true,
            requiresOrg: false,
            requiresStek: false,
            requiresPlatformAdmin: true,
            outsideMode: true,
        },
    },
    {
        path: "/domain",
        name: "domain",
        component: () => import("@/views/Domain.vue"),
    },
    {
        path: "/product",
        name: "product",
        component: () => import("@/views/Product.vue"),
    },
    {
        path: "/process",
        name: "process",
        component: () => import("@/views/Process.vue"),
    },
    {
        path: "/application",
        name: "application",
        component: () => import("@/views/Application.vue"),
    },
    {
        path: "/service",
        name: "service",
        component: () => import("@/views/Service.vue"),
    },
    {
        path: "/docs",
        name: "docsHome",
        component: () => import("@/views/DocsHome.vue"),
        meta: {
            layout: "ApplicationLayout",
            hideSidebar: true,
        },
    },
    {
        path: "/help",
        name: "help",
        component: () => import("@/views/Help.vue"),
        meta: {
            hideSidebar: true,
        },
    },
    {
        path: "/docs/core/live-mode-events",
        name: "liveModeEventsDocs",
        component: () => import("@/views/LiveModeEventsDocs.vue"),
        meta: {
            layout: "ApplicationLayout",
            hideSidebar: true,
        },
    },
    {
        path: "/docs/live-mode-events",
        redirect: "/docs/core/live-mode-events",
    },
    {
        path: "/docs/core/live-mode-event-conventions",
        name: "liveModeEventConventionsDocs",
        component: () => import("@/views/LiveModeEventConventionsDocs.vue"),
        meta: {
            layout: "ApplicationLayout",
            hideSidebar: true,
        },
    },
    {
        path: "/settings",
        component: () => import("@/views/Profile.vue"),
        meta: {
            requiresAuth: true,
            outsideMode: true,
        },
        children: [
            {
                path: "",
                name: "settings",
                redirect: { name: "settings-general" },
            },
            {
                path: "general",
                name: "settings-general",
                component: () => import("@/views/Profile.vue"),
                meta: {
                    requiresAuth: true,
                    outsideMode: true,
                },
            },
            {
                path: "organizations",
                name: "settings-organizations",
                component: () => import("@/views/Profile.vue"),
                meta: {
                    requiresAuth: true,
                    outsideMode: true,
                },
            },
            {
                path: "security",
                name: "settings-security",
                component: () => import("@/views/Profile.vue"),
                meta: {
                    requiresAuth: true,
                    outsideMode: true,
                },
            },
            {
                path: "api-keys",
                name: "settings-api-keys",
                component: () => import("@/views/Profile.vue"),
                meta: {
                    requiresAuth: true,
                    outsideMode: true,
                },
            },
            {
                path: "personal-memory",
                name: "settings-personal-memory",
                component: () => import("@/views/Profile.vue"),
                meta: {
                    requiresAuth: true,
                    outsideMode: true,
                },
            },
        ],
    },
    {
        path: "/oauth/callback",
        name: "oauthCallback",
        component: () => import("@/views/OAuthCallback.vue"),
        meta: {
            requiresStek: false,
            requiresAuth: true,
            requiresOrg: false,
        },
    },
    {
        path: "/profile",
        redirect: "/settings",
    },
    {
        path: "/profile/org/:orgId",
        redirect: (to) => `/settings/org/${to.params.orgId}`,
    },
    {
        path: "/profile/:orgId/:stekId/integrations",
        redirect: (to) =>
            `/settings/${to.params.orgId}/${to.params.stekId}/integrations`,
    },
    {
        path: "/activity-metrics",
        name: "activityMetrics",
        component: () => import("@/views/ActivityMetrics.vue"),
        meta: {
            requiresPlatformAdmin: true,
            requiresAuth: true,
            requiresOrg: false, // Platform-wide
            requiresStek: false,
            outsideMode: true,
            layout: "ApplicationLayout",
        },
    },
    {
        path: "/:orgName?",
        name: "organizationPage",
        component: () => import("@/views/OrgInst.vue"),
        meta: {
            requiresAuth: true,
            requiresOrg: true,
            requiresStek: true,
            layout: "ApplicationLayout",
        },
        children: [
            {
                path: "",
                name: "organizationHome",
                component: () => import("@/views/OrganizationHome.vue"),
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    outsideMode: true,
                },
            },
            {
                path: "stek/create",
                name: "createStek",
                component: () => import("@/views/CreateStek.vue"),
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    outsideMode: true,
                },
            },
            {
                path: "manage/steks",
                redirect: (to) => ({
                    name: "stekManagement",
                    params: { orgName: to.params.orgName },
                }),
            },
            {
                path: "manage/whats-new",
                redirect: () => ({ name: "platformAdmin" }),
            },
            {
                path: "user",
                redirect: (to) => ({
                    name: "userManagement",
                    params: { orgName: to.params.orgName },
                }),
            },
            {
                path: "settings",
                component: () => import("@/views/OrganizationSettings.vue"),
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: false,
                    outsideMode: true,
                },
                children: [
                    {
                        path: "",
                        name: "organizationSettings",
                        // Static on purpose: a redirect function runs during
                        // route resolution, before Pinia exists on a direct
                        // load, so it cannot consult permissions. The Steks
                        // tab stays hidden for users who may not manage steks
                        // and OrganizationSettings.vue steers them to the first
                        // tab they may see.
                        redirect: { name: "stekManagement" },
                    },
                    {
                        path: "steks",
                        name: "stekManagement",
                        component: () => import("@/views/StekManagement.vue"),
                        meta: { requiresStek: false, outsideMode: true },
                    },
                    {
                        path: "users",
                        name: "userManagement",
                        component: () => import("@/views/UserManagement.vue"),
                        meta: { requiresStek: false, outsideMode: true },
                    },
                    {
                        path: "billing",
                        name: "organizationBilling",
                        component: () =>
                            import("@/components/subscription/OrganizationBilling.vue"),
                        meta: { requiresStek: false, outsideMode: true },
                    },
                ],
            },
            {
                path: "steks",
                component: undefined,
                meta: {
                    requiresAuth: true,
                    requiresOrg: true,
                    requiresStek: true,
                },
                children: [
                    {
                        path: ":stekName",
                        component: undefined,
                        children: [
                            {
                                // The personal landing page — "what is worth
                                // doing next". Yours, but scoped to this stek:
                                // everything it surfaces is derived from one
                                // stek's model and one stek's run data, and the
                                // ask box a suggestion opens is itself one
                                // conversation per person per stek. Spanning
                                // steks would have bought nothing and forced a
                                // stek switch on every click.
                                //
                                // Added BESIDE the stek picker, not replacing
                                // it. Whether a signed-in user lands here
                                // rather than on the org page is a real product
                                // decision and should be made deliberately, not
                                // drift in with the page that makes it possible.
                                //
                                // The RAIL ROW is gated on `personal_agent`
                                // (see NavRail); the route deliberately is not,
                                // yet. That capability is what an org has
                                // agreed to let gotoko use ABOUT A PERSON, and
                                // this page reads none of it — it renders
                                // fixtures. Gating the URL too would mean
                                // nobody can review the page until a flag is
                                // flipped, for no privacy gained. When it
                                // starts reading real attention data, this meta
                                // grows the guard.
                                path: "home",
                                name: "personalHome",
                                component: () =>
                                    import("@/views/PersonalHome.vue"),
                                meta: {
                                    outsideMode: true,
                                },
                            },
                            {
                                path: "integrations",
                                name: "integrationManagement",
                                component: () =>
                                    import("@/views/IntegrationManagement.vue"),
                                meta: {
                                    outsideMode: true,
                                },
                            },
                            {
                                path: "settings",
                                name: "stekSettings",
                                component: () =>
                                    import("@/views/StekSettings.vue"),
                                meta: {
                                    outsideMode: true,
                                },
                            },
                            {
                                // Param is `:thread`; a `*Id` param stops the
                                // entity store resolving the stek.
                                path: "threads/:thread",
                                name: "thread",
                                component: () =>
                                    import("@/views/ThreadDetailPage.vue"),
                            },
                            {
                                // Thread-scoped, full-page conflict resolution
                                // ("Reconcile"); reads the thread via the app's
                                // usual `?thread=<id>` query convention (see
                                // ThreadDrawer/ApplicationLayout), not a path param.
                                path: "reconcile",
                                name: "reconcile",
                                component: () =>
                                    import("@/views/ReconcilePage.vue"),
                                // Keeps the sidebar (+ its toggle),
                                // but opts out of `.page`'s --page-margin inset so the
                                // top bar + flush left queue pane reach the edges.
                                meta: {
                                    fullBleed: true,
                                },
                            },
                            {
                                // Stek-level, mode-independent resolution queue
                                // ("Triage"): unresolved external pools today,
                                // more issue types later. Deep-linkable via
                                // `?issue=<id>`. Same full-bleed, kept-sidebar
                                // shell as Reconcile.
                                path: "triage",
                                name: "triage",
                                component: () =>
                                    import("@/views/TriagePage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                    fullBleed: true,
                                },
                            },
                            {
                                // ":version?" powers the History panel (opened
                                // via the HistoryButton) with a read-only list
                                // of the merged changelog; the page itself
                                // stays on live content regardless of the
                                // param. Static siblings ("strategy",
                                // "reconcile", "triage", "domain/...", etc.)
                                // outrank this optional param, so their
                                // navigation is unaffected. The "biz/domain"
                                // alias intentionally has no ":version?".
                                path: ":version?",
                                alias: "biz/domain",
                                name: "stekPage",
                                component: () => import("@/views/StekPage.vue"),
                            },
                            {
                                path: "overview",
                                name: "businessOverview",
                                component: () =>
                                    import("@/views/OverviewPage.vue"),
                            },
                            {
                                // Decides where opening the stek lands, then
                                // replaces itself (see STEK_HOME_ROUTE_NAME).
                                path: "home",
                                name: "stekHome",
                                component: () =>
                                    import("@/views/StekHomePage.vue"),
                            },
                            {
                                // The form without a thread, the sketch with
                                // one. The id is in the path so a reload, the
                                // drawer remounting the page, and accepting
                                // the draft all keep the same sketch.
                                path: "onboarding/:threadId?",
                                name: "stekOnboarding",
                                component: () =>
                                    import("@/views/OnboardingPage.vue"),
                            },
                            {
                                path: "strategy",
                                redirect: { name: "strategyVision" },
                            },
                            {
                                path: "strategy/vision",
                                name: "strategyVision",
                                component: () =>
                                    import("@/views/strategy/StrategyVisionPage.vue"),
                            },
                            {
                                path: "strategy/objectives",
                                name: "strategyObjectives",
                                component: () =>
                                    import("@/views/strategy/StrategyObjectivesPage.vue"),
                            },
                            {
                                path: "strategy/horizons",
                                name: "strategyHorizons",
                                component: () =>
                                    import("@/views/strategy/StrategyHorizonsPage.vue"),
                            },
                            {
                                path: "strategy/watchers",
                                name: "strategyWatchers",
                                component: () =>
                                    import("@/views/strategy/StrategyWatchersPage.vue"),
                            },
                            {
                                path: "strategy/market",
                                redirect: {
                                    name: "strategyMarketCompetitors",
                                },
                            },
                            {
                                path: "strategy/market/competitors",
                                name: "strategyMarketCompetitors",
                                component: () =>
                                    import("@/views/strategy/StrategyMarketPage.vue"),
                            },
                            {
                                path: "strategy/market/trends",
                                name: "strategyMarketTrends",
                                component: () =>
                                    import("@/views/strategy/StrategyMarketPage.vue"),
                            },
                            {
                                path: "live",
                                name: "livePage",
                                component: () =>
                                    import("@/live/views/Page.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "live/domains",
                                name: "liveDomainsPage",
                                component: () =>
                                    import("@/live/views/DomainsPage.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "live/domain/:domainId",
                                name: "liveDomainPage",
                                component: () =>
                                    import("@/live/views/Page.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "live/products",
                                name: "liveProductsPage",
                                component: () =>
                                    import("@/live/views/ProductsPage.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "live/product/:productId",
                                name: "liveProductPage",
                                component: () =>
                                    import("@/live/views/Page.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "live/processes",
                                name: "liveProcessesPage",
                                component: () =>
                                    import("@/live/views/ProcessesPage.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "live/process/:processId",
                                name: "liveProcessPage",
                                component: () =>
                                    import("@/live/views/Page.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "live/process/:processId/instance/:instanceId",
                                name: "processInstancePage",
                                component: () =>
                                    import("@/live/views/ProcessInstancePage.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "live/compliance-evidence",
                                name: "complianceEvidencePage",
                                component: () =>
                                    import("@/live/views/ComplianceEvidencePage.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "live/compliance-evidence-detail",
                                name: "complianceEvidenceDetailPage",
                                component: () =>
                                    import("@/live/views/ComplianceEvidenceDetailPage.vue"),
                                meta: {
                                    mode: "live",
                                },
                            },
                            {
                                path: "dev",
                                name: "devPage",
                                component: () => import("@/views/DevPage.vue"),
                                meta: {
                                    mode: "dev",
                                },
                            },
                            {
                                path: "dev/domain/:domainId",
                                name: "devDomainPage",
                                component: () =>
                                    import("@/views/DevDomainPage.vue"),
                                meta: {
                                    mode: "dev",
                                },
                            },
                            {
                                path: "dev/application/:applicationId/:version?",
                                name: "applicationPage",
                                component: () =>
                                    import("@/views/ApplicationPage.vue"),
                                meta: {
                                    mode: "dev",
                                },
                            },
                            {
                                path: "dev/application/:applicationId/application_entity/:applicationEntityId",
                                name: "applicationEntityPage",
                                component: () =>
                                    import("@/views/ApplicationEntityPage.vue"),
                                meta: {
                                    mode: "dev",
                                },
                            },
                            {
                                path: "build",
                                name: "buildPage",
                                component: () =>
                                    import("@/views/BuildPage.vue"),
                                meta: {
                                    mode: "build",
                                },
                            },
                            {
                                path: "build/compliance",
                                name: "buildCompliance",
                                component: () =>
                                    import("@/views/BuildCompliance.vue"),
                                meta: {
                                    mode: "build",
                                },
                            },
                            {
                                path: "build/agents",
                                name: "buildAgents",
                                component: () =>
                                    import("@/views/BuildAgents.vue"),
                                meta: {
                                    mode: "build",
                                },
                            },
                            {
                                path: "build/agents/:agentId",
                                name: "buildAgentDetail",
                                component: () =>
                                    import("@/views/BuildAgentDetail.vue"),
                                meta: {
                                    mode: "build",
                                },
                            },
                            {
                                path: "build/connections/:consumerId",
                                name: "buildConnection",
                                component: () =>
                                    import("@/views/BuildConnection.vue"),
                                meta: {
                                    mode: "build",
                                },
                            },
                            {
                                path: "external_party/:externalPartyId",
                                name: "externalPartyPage",
                                component: () =>
                                    import("@/views/ExternalPartyPage.vue"),
                                meta: {
                                    mode: "dev",
                                    breadcrumbs: "auto",
                                },
                            },
                            {
                                // ":version?" powers the History panel (opened
                                // via the page's History action). The static
                                // "document" sub-route below outranks this
                                // optional param, so domain-document nav is
                                // unaffected.
                                path: "domain/:domainId/:version?",
                                name: "domainPage",
                                component: () =>
                                    import("@/views/DomainPage.vue"),
                            },
                            {
                                // The domain's object canvas, maximized. A
                                // static sibling like the document route below,
                                // so it outranks domainPage's optional
                                // ":version?" param. Full-bleed (no `.page`
                                // inset) so the diagram reaches the edges of
                                // the content column, with the rail and top bar
                                // kept — same shell as Reconcile and Triage.
                                path: "domain/:domainId/canvas",
                                name: "domainCanvasPage",
                                component: () =>
                                    import("@/views/DomainCanvasPage.vue"),
                                meta: {
                                    breadcrumbs: "auto",
                                    fullBleed: true,
                                },
                            },
                            {
                                path: "domain/:domainId/document/:artefactId?",
                                name: "domainDocumentPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                            },
                            {
                                // ":version?" powers the History panel (opened
                                // via the page's History action); static
                                // sub-routes outrank this optional param.
                                path: "product/:productId/:version?",
                                name: "productPage",
                                component: () =>
                                    import("@/views/ProductPage.vue"),
                            },
                            {
                                path: "role/:roleId/:version?",
                                name: "rolePage",
                                component: () => import("@/views/RolePage.vue"),
                            },
                            {
                                path: "business_entity/:businessEntityId/:version?",
                                name: "businessEntityPage",
                                component: () =>
                                    import("@/views/BusinessEntityPage.vue"),
                            },
                            {
                                path: "domain_term/:domainTermId/:version?",
                                name: "domainTermPage",
                                component: () =>
                                    import("@/views/DomainTermPage.vue"),
                            },
                            {
                                path: "product/:productId/document/:artefactId?",
                                name: "productDocumentPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                            },
                            {
                                // ":version?" powers the History panel (opened
                                // via the HistoryButton); "current"/absent = live.
                                path: "process/:processId/:version?",
                                name: "processPage",
                                component: () =>
                                    import("@/views/ProcessPage.vue"),
                            },
                            {
                                path: "process/:processId/process-definition/definition/:version?",
                                name: "processDefinitionPage",
                                component: () =>
                                    import("@/views/ProcessDefinitionPage.vue"),
                                meta: {
                                    supportsSideBySide: true,
                                },
                            },
                            {
                                path: "process/:processId/document/:artefactId?",
                                name: "processDocumentPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                            },
                            {
                                path: "process/:processId/task/:taskId",
                                name: "taskPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                            },
                            {
                                path: "process/:processId/task/:taskId/document/:artefactId?",
                                name: "taskDocumentPage",
                                component: () =>
                                    import("@/views/RedirectByIdPage.vue"),
                            },
                            {
                                // ":version?" powers the History panel; static
                                // sub-routes outrank this optional param.
                                path: ":elementType/:elementId/:version?",
                                name: "processElementPage",
                                component: () =>
                                    import("@/views/ProcessElementPage.vue"),
                            },
                            {
                                path: "document/:artefactId/:version?",
                                name: "documentPage",
                                component: () =>
                                    import("@/views/DocumentPage.vue"),
                                meta: {
                                    supportsSideBySide: true,
                                },
                            },
                            {
                                path: "biz/domain/:domainName",
                                name: "domainDashboard",
                                component: () =>
                                    import("@/views/RedirectByNamePage.vue"),
                                children: [
                                    {
                                        path: "",
                                        name: "domainOverview",
                                        component: () =>
                                            import("@/views/RedirectByNamePage.vue"),
                                    },
                                    {
                                        path: "roles",
                                        name: "roles",
                                        component: () =>
                                            import("@/views/RedirectByNamePage.vue"),
                                    },
                                    {
                                        path: "entities",
                                        name: "entities",
                                        component: () =>
                                            import("@/views/RedirectByNamePage.vue"),
                                    },
                                    {
                                        path: "files/:filePath?",
                                        name: "domainFiles",
                                        component: () =>
                                            import("@/views/RedirectByNamePage.vue"),
                                    },
                                ],
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/:filePath?",
                                name: "productInst",
                                component: () =>
                                    import("@/views/RedirectByNamePage.vue"),
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/:filePath?",
                                name: "processInst",
                                component: () =>
                                    import("@/views/RedirectByNamePage.vue"),
                                props: true,
                            },
                            {
                                path: "biz/domain/:domainName/product/:productName/process/:processName/tasks/:taskName/:filePath?",
                                name: "taskInst",
                                component: () =>
                                    import("@/views/RedirectByNamePage.vue"),
                                props: true,
                            },
                            {
                                name: "not-found",
                                path: "not-found",
                                component: () => import("@/views/NotFound.vue"),
                            },
                            {
                                path: ":pathMatch(.*)*",
                                component: () => import("@/views/NotFound.vue"),
                            },
                        ],
                    },
                    {
                        path: ":pathMatch(.*)*",
                        component: () => import("@/views/NotFound.vue"),
                    },
                ],
            },
        ],
    },
    {
        path: "/:pathMatch(.*)*",
        component: () => import("@/views/NotFound.vue"),
    },
];

const devRoute: RouteRecordRaw = {
    path: "/dev",
    name: "dev",
    component: () => import("@/views/Dev.vue"),
    meta: {
        requiresAuth: false,
        layout: "DevLayout",
    },
    children: [
        {
            path: "autologin",
            name: "autologin",
            component: () => import("@/views/AutoLogin.vue"),
            meta: {
                requiresAuth: false,
            },
        },
        {
            path: "code-preview",
            name: "code-preview",
            component: () => import("@/views/CodePreviewDemo.vue"),
            meta: {
                requiresAuth: false,
                layout: "DevLayout",
            },
        },
        {
            path: "spinner",
            name: "spinner-demo",
            component: () => import("@/views/SpinnerDemo.vue"),
            meta: {
                requiresAuth: false,
                layout: "DevLayout",
            },
        },
        {
            path: "task-evaluation",
            component: () => import("@/views/TaskEvaluation.vue"),
            meta: {
                requiresAuth: true,
                layout: "DevLayout",
            },
            children: [
                {
                    path: "",
                    name: "task-evaluation",
                    component: () => import("@/views/TaskEvaluationHome.vue"),
                },
                // Generic route pattern for all task types
                {
                    path: ":taskType",
                    name: "task-evaluation-list",
                    component: () => import("@/views/TaskEvaluationList.vue"),
                    props: (route) => {
                        const taskTypeMap = {
                            "generate-description": {
                                apiType: "generate_description",
                                name: "Generate Description",
                            },
                            "generate-definition-xml": {
                                apiType: "generate_definition_xml",
                                name: "Generate BPMN Definition XML",
                            },
                            "generate-ui": {
                                apiType: "generate_user_interface",
                                name: "Generate UI Component",
                            },
                        };
                        const taskInfo =
                            taskTypeMap[route.params.taskType as string];
                        return {
                            taskType: taskInfo.apiType,
                            taskName: taskInfo.name,
                        };
                    },
                },
                {
                    path: ":taskType/configure",
                    name: "task-evaluation-configure",
                    component: () =>
                        import("@/components/TaskEvaluation/GenericTaskConfiguration.vue"),
                    props: (route) => ({
                        taskType: route.params.taskType as string,
                    }),
                },
                {
                    path: ":taskType/:id",
                    name: "task-evaluation-detail",
                    component: () => import("@/views/TaskEvaluationList.vue"),
                    props: (route) => {
                        const taskTypeMap = {
                            "generate-description": {
                                apiType: "generate_description",
                                name: "Generate Description",
                            },
                            "generate-definition-xml": {
                                apiType: "generate_definition_xml",
                                name: "Generate BPMN Definition XML",
                            },
                            "generate-ui": {
                                apiType: "generate_user_interface",
                                name: "Generate UI Component",
                            },
                        };
                        const taskInfo =
                            taskTypeMap[route.params.taskType as string];
                        return {
                            taskType: taskInfo.apiType,
                            taskName: taskInfo.name,
                            evaluationId: route.params.id,
                        };
                    },
                },
            ],
        },
    ],
};

const routes = [...baseRoutes];
if (import.meta.env.MODE !== "production") {
    routes.push(devRoute);
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        // `.main-container` is the scroll container, not the window
        // (ApplicationLayout.vue), so
        // `window.scrollTo` was a no-op and scroll position survived every
        // navigation — arriving on a new page already scrolled to wherever the
        // previous one had been. The window call stays for routes rendered
        // outside that layout (login, OAuth).
        window.scrollTo(0, 0);
        document.querySelector(".main-container")?.scrollTo(0, 0);
    },
});

// Shared by both the one-time boot check below and the recurring
// `beforeEach` guard, so unauthenticated users always get sent to login
// with the same "come back here after signing in" query param, whether
// this is the very first page load or a later in-app (SPA) navigation.
function buildLoginRedirect(to: {
    name?: unknown;
    params?: unknown;
    query?: unknown;
}) {
    return {
        name: "login",
        query: {
            returnTo: encodeURIComponent(
                JSON.stringify({
                    name: to.name,
                    params: to.params,
                    query: to.query,
                })
            ),
        },
    };
}

export let routerIsReady = false;
// Make sure we can do something right after the router is ready.
router.isReady().then(async () => {
    const { authStatus } = useAuthenticationManager();
    const { isInitialized } = useAuthenticationStore();
    routerIsReady = true;

    const requiresAuth = router.currentRoute.value.meta.requiresAuth;
    const currentRouteName = String(router.currentRoute.value.name);

    // Sometimes the authentication library is initialized before the router is,
    // in those cases we still need to redirect to the right pages.
    await isInitialized;
    switch (authStatus.value) {
        case AuthStatus.SIGNED_IN: {
            if (
                ["login", "signup", "forgotPassword"].includes(currentRouteName)
            ) {
                router.push("/");
            }
            break;
        }
        case AuthStatus.MFA_REQUIRED:
        case AuthStatus.MFA_ENROLLMENT_REQUIRED:
        case AuthStatus.EMAIL_VERIFICATION_REQUIRED: {
            if (currentRouteName !== "login") {
                router.push(buildLoginRedirect(router.currentRoute.value));
            }
            break;
        }
        case AuthStatus.NOT_SIGNED_IN: {
            // Special case for root path: redirect to /home instead of /login
            if (router.currentRoute.value.fullPath === "/") {
                router.push("/home");
            } else if (requiresAuth) {
                router.push(buildLoginRedirect(router.currentRoute.value));
            }
            break;
        }
    }
});

const AUTH_RECORDING_ROUTES = ["login", "signup", "forgotPassword"];

// First guard — flush all pending throttled writes before navigation so that
// the sync-store's router.beforeEach can pick up the queued entries.
router.beforeEach(() => {
    flushAll();
});

router.beforeEach(async (guard) => {
    // Start recording when entering auth flows so login and MFA setup are captured.
    // Stop recording when leaving auth routes so sessions don't extend into the app.
    if (AUTH_RECORDING_ROUTES.includes(String(guard.name))) {
        mixpanel.startSessionRecording();
    } else {
        mixpanel.stopSessionRecording();
    }

    mixpanel.trackPageview({
        "Page Name": guard.name,
        "Page URL": guard.path,
        ...guard.params,
    });

    const { isInitialized } = useAuthenticationStore();
    // There is a brief period in which the authentication library is still
    // initializing. In this period the authentication status is unknown.
    // As soon as the authentication status is known it will be updated in
    // the store, so we utilize a short-lived watcher so that we can wait
    // for the authentication status to be updated in the router guard.
    await isInitialized;

    // Special case for root path when not authenticated
    const { authStatus } = useAuthenticationManager();
    if (
        guard.fullPath === "/" &&
        authStatus.value === AuthStatus.NOT_SIGNED_IN
    ) {
        return { path: "/home" };
    }

    // Guard `requiresAuth` routes on every navigation, not just the initial
    // load — otherwise an in-app SPA navigation (e.g. router.push from a nav
    // menu) to a protected route while signed out would resolve/render
    // instead of bouncing to login.
    if (
        guard.meta.requiresAuth &&
        authStatus.value === AuthStatus.NOT_SIGNED_IN
    ) {
        return buildLoginRedirect(guard);
    }
});

// Platform Admin pages are gated on the platform-wide ``is_platform_admin``
// flag from /user/me — see platform-admin-guard.ts for why the guard fetches
// the profile itself on a direct load.
router.beforeEach((guard) => {
    if (!guard.meta.requiresPlatformAdmin) {
        return;
    }
    const userStore = useUserStore();
    const { user } = storeToRefs(userStore);
    return platformAdminGuard(guard, {
        user,
        fetchUser: userStore.updateUser,
    });
});

export default router;
